import { ROLES, type OpRole } from '@hirebotiq-ops/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { useAuth } from '../hooks/useAuth.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsers,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type RoleFilter = 'all' | OpRole;

function AdminUsers() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<RoleFilter>('all');
  const utils = trpc.useUtils();
  const list = trpc.admin.listUsers.useQuery(filter === 'all' ? undefined : { role: filter });
  const setRole = trpc.admin.setUserRole.useMutation({
    onSuccess: () => {
      utils.admin.listUsers.invalidate();
      utils.admin.overview.invalidate();
      toast.success('Rolle aktualisiert');
    },
    onError: (err) => toast.error(err.message),
  });
  // OPS-D4: Worker-Profil für einen bestehenden User öffnen ODER anlegen
  // (get-or-create via staffEnsure — vorhandenes Profil wird geöffnet statt 409).
  const createWorker = trpc.workers.staffEnsure.useMutation({
    onSuccess: (w) => {
      void navigate({ to: '/admin/workers/$id', params: { id: w.id } });
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-bold">User & Rollen</h1>
          <p className="text-[var(--muted-foreground)]">
            Eigene Rolle ist gesperrt (Lockout-Schutz)
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
            <CardDescription>Nur User mit ausgewählter Rolle anzeigen</CardDescription>
          </CardHeader>
          <CardContent className="max-w-xs">
            <Select value={filter} onValueChange={(v) => setFilter(v as RoleFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Rollen</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Letzter Login</TableHead>
                  <TableHead>Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Lade …
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.map((u) => {
                  const isSelf = u.id === me?.id;
                  const isTechnicalClientView = u.loginMethod === 'impersonation';
                  return (
                    <TableRow key={u.id}>
                      <TableCell>{u.name ?? '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{u.email ?? '—'}</TableCell>
                      <TableCell className="flex items-center gap-2">
                        <Select
                          value={u.role}
                          disabled={isSelf || isTechnicalClientView || setRole.isPending}
                          onValueChange={(v) => setRole.mutate({ userId: u.id, role: v as OpRole })}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r} value={r}>
                                {r}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isSelf && (
                          <span className="text-xs text-[var(--muted-foreground)]">
                            (du selbst)
                          </span>
                        )}
                        {isTechnicalClientView && (
                          <span className="text-xs text-[var(--muted-foreground)]">
                            (technische Kundensicht)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-[var(--muted-foreground)]">
                        {u.loginMethod === 'impersonation'
                          ? 'Technische Kundensicht – kein Login'
                          : new Date(u.lastSignedIn).toLocaleString('de-DE')}
                        <span className="ml-2">
                          <StatusBadge status={u.role} />
                        </span>
                      </TableCell>
                      <TableCell>
                        {(u.role === 'pending' || u.role === 'worker') && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={createWorker.isPending}
                            onClick={() => createWorker.mutate({ userId: u.id })}
                            title="Worker-Profil für diesen User anlegen (bei vorhandenem Profil: öffnen)"
                          >
                            + Worker-Profil
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
